@import 'apps/webcomponents/src/assets/scss/mixins';

* {
  box-sizing: border-box;
}

:host {
  width: auto;
  height: auto;
  display: inline-block;
  --cursorType: pointer;
  --wrapper-padding: var(--spacer);

  &:host(:not([disabled])) {
    .wrapper {
      a {
        &:focus-visible {
          outline: none;
          @include focus-outline-state-mixin(var(--blue-layout-500-focus));
        }
      }
    }
  }
  &:host([disabled]) {
    .wrapper {
      a {
        &:focus-visible {
          outline: none;
        }
      }
    }
  }
}

::slotted(*) {
  width: auto;
  height: auto;
}
.wrapper,
.wrapper a {
  display: var(--display, flex);
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  cursor: var(--cursorType);
  font-family: var(--font-family);
}

.wrapper {
  max-width: 100%;
  width: fit-content;
  padding: 0 calc(var(--wrapper-padding) / 4);

  .icon-element {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: calc(var(--spacer) + 2px);
  }
}

:host([color='dark']) {
  .wrapper,
  .wrapper a {
    color: var(--black-layout-701-primary);
  }

  .wrapper {
    &:hover {
      color: var(--chart-304-icon-buttons);
    }

    &:active {
      color: var(--black-layout-701-primary);
    }

    &:focus {
      color: var(--black-layout-701-primary);
    }
  }
  &:host([disabled]) {
    .wrapper,
    .wrapper a {
      color: var(--grey-layout-201-disabled-text);
    }
  }
}

:host([color='light']) {
  .wrapper,
  .wrapper a {
    color: var(--white-layout-300-primary);
  }

  .wrapper {
    &:hover {
      color: var(--white-layout-201-secondary-on-dark-background);
    }

    &:active {
      color: var(--white-layout-300-primary);
    }

    &:focus {
      color: var(--white-layout-300-primary);
    }
  }
  &:host([disabled]) {
    .wrapper,
    .wrapper a {
      color: var(--white-layout-101-outline-on-dark-background);
    }
  }
}

:host([color='secondary']) {
  .wrapper,
  .wrapper a {
    color: var(--body-text-arrows-400);
  }

  .wrapper,
  .wrapper a {
    &:hover {
      color: var(--chart-301-icon-buttons);
    }

    &:active {
      color: var(--body-text-arrows-400);
    }

    &:focus,
    &:focus-within {
      color: var(--body-text-arrows-400);
      background-color: var(--blue-layout-302-active-focus);
    }
  }
  &:host([disabled]) {
    .wrapper,
    .wrapper a {
      color: var(--grey-layout-200-disabled);
    }
  }
}

:host([color='negative']) {
  .wrapper,
  .wrapper a {
    color: var(--red-layout-300-primary);
  }

  .wrapper,
  .wrapper a {
    &:hover {
      color: var(--red-layout-303-primary);
    }

    &:active {
      color: var(--red-layout-300-primary);
    }

    &:focus,
    &:focus-visible {
      color: var(--red-layout-300-primary);
    }
  }
}

:host([color='primary']) {
  .wrapper,
  .wrapper a {
    color: var(--blue-layout-400-primary);
  }

  .wrapper,
  .wrapper a {
    &:hover {
      color: var(--blue-layout-301-active);
    }
    &:active,
    &:focus,
    &:focus-visible {
      color: var(--blue-layout-503-focus-on-dark-background-hc);
    }
  }
  &:host([disabled]) {
    .wrapper,
    .wrapper a {
      color: var(--blue-layout-202-disabled);
    }
  }
}

:host([type='underline']) {
  .wrapper {
    ::slotted(*) {
      text-decoration: underline;
    }
  }
}

:host([type='button']) {
  .wrapper {
    a {
      cursor: pointer;
      font-size: var(--button-font-size);
      line-height: 1;
      border-radius: var(--button-border-radius);
      text-align: center;
      padding: 0 calc(2 * var(--spacer));
      transition: all 0.3s ease;
      height: var(--button-height);
      min-width: calc(2.25 * var(--button-height));
      max-width: var(--button-max-width, unset);
      font-weight: 500;
      background-color: transparent;
      border: 1px solid;
      border-color: inherit;
      text-transform: var(--button-text-transform, uppercase);
    }
  }
}

:host([type='button'][color='primary']) {
  .wrapper {
    a {
      border-color: var(--blue-layout-403-primary);
      color: var(--white-layout-300-primary);
      background-color: var(--blue-layout-403-primary);

      &:hover {
        border-color: var(--blue-layout-301-active);
        background-color: var(--blue-layout-301-active);
      }

      &:focus,
      &:focus-visible {
        border-color: var(--blue-layout-501-focus);
        background-color: var(--blue-layout-501-focus);
      }

      &:active {
        border-color: var(--blue-layout-501-focus);
        background-color: var(--blue-layout-501-focus);
      }
    }
  }
}

:host([type='button'][color='secondary']) {
  .wrapper {
    a {
      border-color: var(--body-text-arrows-400);
      color: var(--body-text-arrows-400);

      &:hover {
        background-color: var(--chart-050-hover);
      }

      &:focus,
      &:focus-visible {
        @include focus-outline-state-mixin(var(--blue-layout-500-focus));
      }

      &:active {
        color: var(--blue-layout-400-primary);
        border-color: var(--blue-layout-400-primary);
        background-color: transparent;
      }
    }
  }
  &:host([fill='outline']) {
    .wrapper {
      a {
        background-color: var(--white-layout-300-primary);
        border-color: var(--white-layout-300-primary);

        &:active {
          color: var(--body-text-arrows-400);
          border-color: var(--white-layout-300-primary);
          background-color: var(--white-layout-300-primary);
        }
      }
    }
  }
}

$iconSize: 11, 12, 13, 14, 16, 18, 20, 24, 32, 36, 48;

$smallFontSize: 11, 12, 13;
$fontSize: 14, 16, 18, 20, 24, 32, 36, 48;
$fontWeight: 300, 400, 500, 600, 700;

@each $size in $iconSize {
  :host([iconSize='#{$size}']) {
    .icon-element {
      font-size: #{$size}px;
    }
  }
}

@each $size in $fontSize {
  :host([fontSize='#{$size}']) {
    font-size: calc(#{$size}px + var(--dynamic-font-size-changer));
  }
}

@each $size in $smallFontSize {
  :host([fontSize='#{$size}']) {
    font-size: calc(#{$size}px + var(--dynamic-small-font-size-changer));
  }
}

@each $weight in $fontWeight {
  :host([fontWeight='#{$weight}']) {
    font-weight: #{$weight};
  }
}
